import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import {
  CopyPlan,
  type CreatePlan,
  type UpdatePlan,
  type PlanTask
} from '../index'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

const invalidatePlanQueries = async (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['plans'] }),
    queryClient.invalidateQueries({ queryKey: ['planLegislation'] }),
    queryClient.invalidateQueries({ queryKey: ['tasks'] }),
    queryClient.invalidateQueries({ queryKey: ['taskDelegation'] }),
    queryClient.invalidateQueries({ queryKey: ['moveDestinations'] }),
    queryClient.invalidateQueries({ queryKey: ['planSummaries'] }),
    queryClient.invalidateQueries({ queryKey: ['sample-taking-report'] })
  ])
}

export const useCreatePlanMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (plan: CreatePlan) => {
      const { data, error } = await authFetch().POST('/plans', {
        body: plan
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await invalidatePlanQueries(queryClient)
    }
  })
}

interface UpdatePlanMutationVariables {
  planId: number
  etag: string
  body: UpdatePlan
}

export const useUpdatePlanMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ planId, etag, body }: UpdatePlanMutationVariables) => {
      const { data, error } = await authFetch().PUT('/plans/{planId}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: { planId }
        },
        body
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (plan, variables) => {
      queryClient.setQueryData(['plans', variables.planId], plan)
      await invalidatePlanQueries(queryClient)
    }
  })
}

export const useGetPlansQuery = (isEnabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['plans'],
    enabled: isEnabled,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/plans')
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

export const getPlans = async (
  authFetch: ReturnType<typeof useAuth>['authFetch']
) => {
  const { data, error } = await authFetch().GET('/plans')
  if (error) throwOnError(error)
  return data
}

export const getPlanById = async (
  authFetch: ReturnType<typeof useAuth>['authFetch'],
  planId: number
) => {
  const { data, error } = await authFetch().GET('/plans/{planId}', {
    params: { path: { planId } }
  })
  if (error) throwOnError(error)
  return data
}

export const useGetPlanByIdQuery = (planId?: number, enabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['plans', planId],
    enabled: enabled && planId != null && planId > 0,
    staleTime: 60_000,
    queryFn:
      planId != null && planId > 0
        ? async () => {
            return getPlanById(authFetch, planId)
          }
        : skipToken
  })
}

export const useGetPlanSampleSummaryQuery = (planId?: number) => {
  const { authFetchV3 } = useAuth()
  return useQuery({
    queryKey: ['plans', planId, 'sample-summary'],
    queryFn:
      planId != null && planId > 0
        ? async () => {
            const { data, error } = await authFetchV3().GET(
              '/plans/{planId}/sample-summary',
              { params: { path: { planId } } }
            )
            if (error) throwOnError(error)
            return data
          }
        : skipToken
  })
}

export const useGetWorkspacesQuery = (isEnabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['workspaces'],
    enabled: isEnabled,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/workspaces')
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

export const useGetWorkspaceGroupsQuery = (isEnabled = true) => {
  const { authFetch, userId, permissions, isAdmin } = useAuth()
  return useQuery({
    queryKey: ['workspace-groups', userId, permissions, isAdmin],
    enabled: isEnabled,
    queryFn: async ({ signal }) => {
      const { data, error } = await authFetch().GET('/workspace-groups', {
        signal
      })
      if (error) throwOnError(error)
      return data
    }
  })
}

interface DeletePlanMutationVariables {
  planId: number
  etag: string
}

export const useDeletePlanMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ planId, etag }: DeletePlanMutationVariables) =>
      authFetch().DELETE('/plans/{planId}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: { planId }
        }
      }),
    onSuccess: async () => {
      await invalidatePlanQueries(queryClient)
    }
  })
}

interface CopyPlanMutationVariables {
  planId: number
  body: CopyPlan
}

export const useCopyPlanMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ planId, body }: CopyPlanMutationVariables) => {
      const { data, error } = await authFetch().POST('/plans/copy/{planId}', {
        params: { path: { planId } },
        body
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => invalidatePlanQueries(queryClient)
  })
}

interface AddTaskToPlanMutationVariables {
  planId: number
  body: PlanTask
}

export const useAddTaskToPlanMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ planId, body }: AddTaskToPlanMutationVariables) => {
      const { data, error } = await authFetch().POST('/plans/{planId}/tasks', {
        params: {
          path: { planId }
        },
        body
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await invalidatePlanQueries(queryClient)
    }
  })
}

export const useMoveTaskMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      planId,
      taskId,
      destinationPlanId,
      etag
    }: {
      planId: number
      taskId: number
      destinationPlanId: number
      etag: string
    }) => {
      const { error } = await authFetch().POST(
        '/plans/{planId}/tasks/{taskId}/move',
        {
          params: { path: { planId, taskId }, header: { 'If-Match': etag } },
          body: { destinationPlanId }
        }
      )
      if (error) throwOnError(error)
    },
    onSuccess: async () => {
      await Promise.all([
        invalidatePlanQueries(queryClient),
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['taskDelegation'] }),
        queryClient.invalidateQueries({ queryKey: ['samples'] })
      ])
    }
  })
}
export const useMoveDestinationsQuery = (
  kind: 'plan' | 'task',
  id?: number
) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['moveDestinations', kind, id],
    enabled: id != null,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const result =
        kind === 'plan'
          ? await authFetch().GET('/plans/{planId}/move-destinations', {
              params: { path: { planId: id! } }
            })
          : await authFetch().GET('/tasks/{taskId}/move-destinations', {
              params: { path: { taskId: id! } }
            })
      if (result.error) throwOnError(result.error)
      return result.data ?? []
    }
  })
}

export const useMovePlanMutation = () => {
  const { authFetch } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      planId,
      sourceParentId,
      destinationPlanId,
      etag
    }: {
      planId: number
      sourceParentId: number
      destinationPlanId: number
      etag: string
    }) => {
      const { error } = await authFetch().POST('/plans/{planId}/move', {
        params: { path: { planId }, header: { 'If-Match': etag } },
        body: { sourceParentId, destinationPlanId }
      })
      if (error) throwOnError(error)
    },
    onSuccess: async () => {
      await Promise.all([
        invalidatePlanQueries(queryClient),
        queryClient.invalidateQueries({ queryKey: ['moveDestinations'] })
      ])
    }
  })
}

export const useGetPlanSummariesQuery = (isEnabled = true) => {
  const { authFetchV4 } = useAuth()
  return useQuery({
    queryKey: ['planSummaries'],
    enabled: isEnabled,
    queryFn: async () => {
      const { data, error } = await authFetchV4().GET('/plans', {
        headers: {
          Accept: 'application/vnd.mattilsynet.proveta.v4.summary+json'
        }
      })
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}
